import { PatientDetailsFormFields, Resource, SignatureFileEntry, SignatureFormFields } from 'common';
import { FullScreenSignDialogComponent } from '../../../full-screen-sign-dialog.component';
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { FileService } from '../../../../../services/file.service';
import { Subscription, merge, startWith, take } from 'rxjs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { NgClass } from '@angular/common';


@Component({
  selector: 'lib-customer-signature-pages',
  standalone: true,
  imports: [MatTabsModule, MatCheckboxModule, MatButtonModule, MatIconModule, FormsModule, NgClass],
  template: `
  <mat-tab-group (selectedTabChange)="markAsTouched($event)">

    <!-- Quotation -->
    <mat-tab [labelClass]="isTabTouched('quotation') && !isTabValid('quotation') ? 'background-pulse-error' : ''">
      <ng-template mat-tab-label>
        Quotation
        
        @if(!disabled && isTabTouched('quotation')) {
          <mat-icon [@fadeInOut] 
            [ngClass]="{'valid-icon': isTabValid('quotation'), 'warn-icon': !isTabValid('quotation')}"
          >{{isTabValid('quotation') ? 'check_circle' : 'priority_high'}}</mat-icon>
        }
      </ng-template>
      <mat-checkbox color="primary" class="my-3" 
        [disabled]="disabled"
        [(ngModel)]="signatures()['quotation'].checked"
        (ngModelChange)="updateSignatureState('quotation')"
        >
          I hereby accept the quotation for credit from Specsfin (PTY) LTD. And agree to the terms and conditions contained therein.
      </mat-checkbox>

    @if (signatures()['quotation'].checked) {
    <div class="my-2">
      @if(signatures()['quotation'].base64 || signatures()['quotation'].serverUrl) {
        <img [src]="signatures()['quotation'].base64 || signatures()['quotation'].serverUrl" alt="Signature" class="signature-border">
      }
      <div class="d-flex">
        <button mat-raised-button (click)="openDialog('quotation')" color="primary"
          [disabled]="disabled || customerSignatureEntry()['quotation']?.isUploading"
        ><mat-icon>edit</mat-icon>{{signatures()['quotation'].base64 ? 'Update' : 'Sign here'}}</button>

        @if (customerSignatureEntry()['quotation']?.isFailed) {
            <span class="flex-center px-3 error-text">Error uploading signature. Please try again.</span>
            <button mat-raised-button color="warn" (click)="uploadSignatureToServer('quotation')" [disabled]="customerSignatureEntry()['quotation']?.isUploading">Try again<mat-icon iconPositionEnd>warning</mat-icon></button>
          }
      </div>
    </div>
    }
    </mat-tab>

    <!-- Terms and Conditions -->
    <mat-tab [labelClass]="isTabTouched('termsAndConditionsAll') && !isTabValid('termsAndConditionsAll') ? 'background-pulse-error' : ''">
      <ng-template mat-tab-label>
        Terms and Conditions
        
        @if(!disabled && isTabTouched('termsAndConditionsAll')) {
          <mat-icon [@fadeInOut] 
            [ngClass]="{'valid-icon': isTabValid('termsAndConditionsAll'), 'warn-icon': !isTabValid('termsAndConditionsAll')}"
          >{{isTabValid('termsAndConditionsAll') ? 'check_circle' : 'priority_high'}}</mat-icon>
        }
      </ng-template>

      <mat-checkbox color="primary" class="my-3" 
        [disabled]="disabled"
        [(ngModel)]="signatures()['termsAndConditions1'].checked"
        (ngModelChange)="updateSignatureState('termsAndConditionsAll', 'termsAndConditions1')"
      >
        I agree that the first instalment shall be due, owing and payable on the next pay date as verified
        by my employer, and every instalment thereafter shall be payable on the following pay dates, until
        the full amount including costs and charges is paid.
      </mat-checkbox>

      <mat-checkbox color="primary" class="my-2" 
        [disabled]="disabled"
        [(ngModel)]="signatures()['termsAndConditions2'].checked"
        (ngModelChange)="updateSignatureState('termsAndConditionsAll', 'termsAndConditions2')"
      >
        I consent to being marketed to, at my employment address for the sale of any product for credit or
        cash in person, telephonically, by sms or any other media.
      </mat-checkbox>

      <mat-checkbox color="primary" class="my-2" 
        [disabled]="disabled"
        [(ngModel)]="signatures()['termsAndConditions3'].checked"
        (ngModelChange)="updateSignatureState('termsAndConditionsAll', 'termsAndConditions3')"
      >
        I warrant that I have not been under administration or debt review and that I have never been insolvent.
      </mat-checkbox>

      <mat-checkbox color="primary" class="my-2" 
        [disabled]="disabled"
        [(ngModel)]="signatures()['termsAndConditions4'].checked"
        (ngModelChange)="updateSignatureState('termsAndConditionsAll', 'termsAndConditions4')"
      >
        I hereby bind myself to the Terms and Conditions of this Agreement, comprising Parts A, B and C,
        which I have read and understand, and which is also accepted by the Practitioner and Specsfin.
      </mat-checkbox>

      <div>
        <mat-checkbox color="primary" class="ml-4"
          [disabled]="disabled"
          [indeterminate]="someTermsComplete()"
          (ngModelChange)="updateSignatureState('termsAndConditionsAll', 'termsAndConditionsAll')"
          [(ngModel)]="signatures()['termsAndConditionsAll'].checked"
          (change)="toggleAllTermsCheckboxToggled($event.checked)"
        >Yes to all the above</mat-checkbox>
      </div>
      
      @if(allRequiredTermsAreChecked()) {
        <div class="my-2">
          @if(signatures()['termsAndConditionsAll'].base64 || signatures()['termsAndConditionsAll'].serverUrl) {
            <img [src]="signatures()['termsAndConditionsAll'].base64 || signatures()['termsAndConditionsAll'].serverUrl" alt="Signature" class="signature-border">
          }
          <div class="d-flex">
            <button mat-raised-button (click)="openDialog('termsAndConditionsAll')" color="primary"
              [disabled]="disabled || customerSignatureEntry()['termsAndConditionsAll']?.isUploading"
            ><mat-icon>edit</mat-icon>{{signatures()['termsAndConditionsAll'].base64 ? 'Update' : 'Sign here'}}</button>

            @if (customerSignatureEntry()['termsAndConditionsAll']?.isFailed) {
              <span class="flex-center px-3 error-text">Error uploading signature. Please try again.</span>
              <button mat-raised-button color="warn" (click)="uploadSignatureToServer('termsAndConditionsAll')" [disabled]="customerSignatureEntry()['termsAndConditionsAll']?.isUploading">Try again<mat-icon iconPositionEnd>warning</mat-icon></button>
            }
          </div>
        </div>
      }
    </mat-tab>
    
    <!-- Debit Order Mandate -->
    <mat-tab [labelClass]="isTabTouched('debitOrderMandate') && !isTabValid('debitOrderMandate') ? 'background-pulse-error' : ''">
      <ng-template mat-tab-label>
        Debit Order Mandate
        
        @if(!disabled && isTabTouched('debitOrderMandate')) {
          <mat-icon [@fadeInOut] 
            [ngClass]="{'valid-icon': isTabValid('debitOrderMandate'), 'warn-icon': !isTabValid('debitOrderMandate')}"
          >{{isTabValid('debitOrderMandate') ? 'check_circle' : 'priority_high'}}</mat-icon>
        }
      </ng-template>
    
      <mat-checkbox color="primary" class="my-3" 
        [disabled]="disabled"
        [(ngModel)]="signatures()['debitOrderMandate'].checked"
        (ngModelChange)="updateSignatureState('debitOrderMandate', 'debitOrderMandate')"
      >I hereby accept the terms and conditions of the Specsfin (PTY) LTD Debit Order Mandate.
      </mat-checkbox>

    @if (signatures()['debitOrderMandate'].checked) {
    <div class="my-2">
      @if(signatures()['debitOrderMandate'].base64 || signatures()['debitOrderMandate'].serverUrl) {
        <img [src]="signatures()['debitOrderMandate'].base64 || signatures()['debitOrderMandate'].serverUrl" alt="Signature" class="signature-border">
      }
      <div class="d-flex">
        <button mat-raised-button (click)="openDialog('debitOrderMandate')" color="primary"
          [disabled]="disabled || customerSignatureEntry()['debitOrderMandate']?.isUploading"
        ><mat-icon>edit</mat-icon>{{signatures()['debitOrderMandate'].base64 ? 'Update' : 'Sign here'}}</button>


        @if (customerSignatureEntry()['debitOrderMandate']?.isFailed) {
          <span class="flex-center px-3 error-text">Error uploading signature. Please try again.</span>
          <button mat-raised-button color="warn" (click)="uploadSignatureToServer('debitOrderMandate')" [disabled]="customerSignatureEntry()['debitOrderMandate']?.isUploading">Try again<mat-icon iconPositionEnd>warning</mat-icon></button>
        }
      </div>
    </div>
    }
    </mat-tab>
  </mat-tab-group>

  `,
  styles: `

  mat-checkbox {
    display: block;
  }

  img {
    margin-bottom: 0.5rem;
  }

  .error-text{
    color: crimson;
    background: rgb(237 20 61 / 10%);
  }

  .not-allowed {
    &:hover {
      cursor: not-allowed;
    }
  }

  .signature-border {
    border: 1px solid #000;
    background: #ccc;
    object-fit: contain;
    max-width: 100%;
    max-height: 200px;
    display: block;
  }

  ::ng-deep {

    .background-pulse-error {
      background-color: rgb(237 20 61 / 15%) !important;
      transition: background-color 0.3s;
      animation: glow 1.75s infinite alternate;

      &:hover {
        background-color: rgb(237 20 61 / 10%) !important;
      }
    }

    .dark-theme {
      .signature-border {
        border: 2px solid rgb(233 233 233 / 42%) !important;
        background-color: #878787;
      }
    }
  }


  @keyframes glow {
    from {
      box-shadow: 0 0 10px -10px rgb(237 20 61 / 10%) inset;
    }
    to {
      box-shadow: 0 0 10px 10px rgb(237 20 61 / 20%) inset;
    }
  }
  `,
  animations: [
    trigger(
      'fadeInOut',
      [
        transition(
          ':enter', [
          style({ opacity: 0 }),
          animate('0.3s ease-out',
            style({ opacity: 1 }))
        ]),
        transition(
          ':leave', [
          style({ opacity: 1 }),
          animate('0.3s ease-in',
            style({ opacity: 0 }))
        ])
      ]
    )
  ]
})
export class CustomerSignaturePagesComponent implements OnChanges {

  //#region Variables
  // Variables for this component that are within scope of "this" component.

  @Input()
  public disabled = false;

  @Input({ required: true })
  public form!: RelevantFormGroup;

  public allRequiredTermsAreChecked = signal(false);

  public signatures = signal<SignatureMap>({
    'quotation': { checked: false, required: true },
    'termsAndConditions1': { checked: false, required: true, booleanFormFields: ['agreeFirstInstalment'] },
    'termsAndConditions2': { checked: false, required: false, booleanFormFields: ['agreeMarketing'] },
    'termsAndConditions3': { checked: false, required: true, booleanFormFields: ['notUnderReview'] },
    'termsAndConditions4': { checked: false, required: true, booleanFormFields: ['agreeTermsSpecsfin'] },
    'termsAndConditionsAll': { checked: false, required: false, booleanFormFields: ['agreeFirstInstalment', 'agreeMarketing', 'notUnderReview', 'agreeTermsSpecsfin'] },
    'debitOrderMandate': { checked: false, required: true, booleanFormFields: ['agreeMandate'] },
  });

  public customerSignatureEntry = signal<Record<string, SignatureFileEntry | null>>({
    'quotation': null,
    'termsAndConditionsAll': null,
    'debitOrderMandate': null,
  });

  public previousTabIndex = 0;
  private formChangesSub: Subscription | null = null;

  //#endregion

  private fileService = inject(FileService);
  public snackbar = inject(MatSnackBar);
  public dialog = inject(MatDialog);

  //#region Instantiation
  // Setup\fetches\initialization.

  //#endregion
  //#region User Interactions
  // Callbacks from html, anything the user can "interact" with from the view.

  public openDialog(key: PrimarySignatureKey) {
    // TODO: Can make additional feature where on checking the checkbox, the signature is automatically opened?
    const dialogRef = this.dialog.open(FullScreenSignDialogComponent, {
      height: '95%',
      width: '95%',
      maxHeight: 'unset',
      maxWidth: 'unset',
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(base64 => {
        const signatures = this.signatures();
        const resourceField = this.toResourceField(key);
        const identityField = this.toIdField(key);

        if (!base64) {
          signatures[key].base64 = undefined;
          this.form.controls['signatures'].controls[resourceField].setValue(null);
          this.form.controls['signatures'].controls[identityField].setValue(null);
        } else {
          signatures[key].base64 = base64;
          const customerSignatureEntry = this.customerSignatureEntry();
          customerSignatureEntry[key] = {
            base64,
            filename: this.toFileName(key),
            isUploadedToServer: false,
          };
          this.customerSignatureEntry.set(customerSignatureEntry);

          this.uploadSignatureToServer(key);
        }

        this.signatures.set(signatures);
      });
  }

  public uploadSignatureToServer(key: PrimarySignatureKey) {
    // Should always have a signature file before calling this method.
    const signatureFile = this.customerSignatureEntry()[key];
    const resourceField = this.toResourceField(key);
    const identityField = this.toIdField(key);

    if (!signatureFile || !signatureFile.base64) return;

    this.fileService.uploadSignatureToServer(signatureFile, signatureFile.base64, signatureFile.filename)
      .then(response => {
        (this.form.controls['signatures'].controls[resourceField] as FormControl<Resource | null>).setValue(response?.resource || null);
        (this.form.controls['signatures'].controls[identityField] as FormControl<number | null>).setValue(response.resource?.id || null);
        this.form.controls['signatures'].controls[resourceField]?.markAsTouched();
        this.form.controls['signatures'].controls[identityField]?.markAsTouched();
        this.markAsDirtyIfAllUploaded(resourceField, identityField);
        this.snackbar.open('Signature captured.', 'Close', { duration: 3000 });
      })
      .catch(error => {
        console.error('Error uploading signature:', error);
        this.form.controls['signatures'].controls[resourceField]?.markAsTouched();
        this.form.controls['signatures'].controls[identityField]?.markAsTouched();
        this.snackbar.open('Error uploading signature. Please try again.', 'Close', { duration: 3000 });
      });
  }

  private markAsDirtyIfAllUploaded(identityField: keyof SignatureFormFields, resourceField: keyof SignatureFormFields) {
    const customerSignatureEntry = this.customerSignatureEntry();
    const allUploaded = Object.values(customerSignatureEntry).every(entry => entry?.isUploadedToServer);

    if (allUploaded) {
      this.form.controls['signatures'].markAsDirty();
      this.form.controls['signatures'].controls[resourceField]?.markAsDirty();
      this.form.controls['signatures'].controls[identityField]?.markAsDirty();
    }
  }

  //#endregion
  //#region Events and Domain Functions
  // $watches/$on events, functions which don't fall into any of the other regions,
  // including any function that has to do domain functionality.

  public markAsTouched(event: MatTabChangeEvent) {
    const tabsToPrimarySignatureKey: Record<number, PrimarySignatureKey> = {
      0: 'quotation',
      1: 'termsAndConditionsAll',
      2: 'debitOrderMandate',
    };

    const key = tabsToPrimarySignatureKey[this.previousTabIndex];
    const resourceField = this.toResourceField(key);
    const identityField = this.toIdField(key);

    this.form.controls['signatures'].controls[resourceField].markAsTouched();
    this.form.controls['signatures'].controls[identityField].markAsTouched();

    this.previousTabIndex = event.index;
  }

  /**
   * Updates the all complete checkbox for the terms and conditions.
   *
   * @param {PrimarySignatureKey} signatureStep - The step to reset the signature should they have changed the checkbox.
   * @param {SignatureMapKey} checkedControl - The control that was checked.
   */
  public updateSignatureState(signatureStep: PrimarySignatureKey, checkedControl?: SignatureMapKey) {
    setTimeout(() => { // Run in the next tick to ensure the checkbox is updated.

      // Update the termsAndCondition checkboxes.
      const signatures = this.signatures();
      const termsKeys = Object.keys(signatures).filter(k => k.startsWith('termsAndConditions') && k !== 'termsAndConditionsAll') as SignatureMapKey[];
      signatures['termsAndConditionsAll'].checked = termsKeys.every(k => signatures[k].checked);
      signatures['termsAndConditionsAll'].indeterminate = !signatures['termsAndConditionsAll'].checked && termsKeys.some(k => signatures[k].checked);

      const allRequiredTermsAreChecked = termsKeys.every(k => (signatures[k].required && signatures[k].checked) || !signatures[k].required);
      this.allRequiredTermsAreChecked.set(allRequiredTermsAreChecked);

      if (checkedControl) {
        const mapEntry = signatures[checkedControl];
        mapEntry.booleanFormFields?.forEach(formControlName => {
          this.form.controls['signatures'].controls[formControlName].setValue(signatures[checkedControl].checked);
        })
      }

      // Reset the signature if a checkbox is changed.
      const wasSetBefore = signatures[signatureStep].base64 !== undefined || signatures[signatureStep].serverUrl !== undefined;
      signatures[signatureStep].base64 = undefined;
      signatures[signatureStep].serverUrl = undefined;
      if (wasSetBefore) {
        // Inform the user.
        this.snackbar.open('Agreement changed - Please sign again.', 'Close', { duration: 5000 });

        // Update validators.
        const resourceField = this.toResourceField(signatureStep);
        const identityField = this.toIdField(signatureStep);
        this.form.controls['signatures'].controls[resourceField].setValue(null);
        this.form.controls['signatures'].controls[identityField].setValue(null);
      }

      this.signatures.set(signatures);
    });
  }

  public toggleAllTermsCheckboxToggled(completed: boolean) {
    const signatures = this.signatures();
    const keys = Object.keys(signatures).filter(k => k.startsWith('termsAndConditions')) as SignatureMapKey[];
    keys.forEach(k => signatures[k].checked = completed);
  }

  //#endregion
  //#region Data Access & Subscriptions
  // API interactions, NGRX selects, etc.

  //#endregion
  //#region Setters, Updaters and Preloaders
  // Setter functions, and methods called from the Instantiation region.

  //#endregion
  //#region Getters and Filters
  // Getter functions and filter methods/functions.

  public someTermsComplete(): boolean {
    const signatures = this.signatures();
    const keys = Object.keys(signatures).filter(k => k.startsWith('termsAndConditions')) as SignatureMapKey[];
    return !signatures['termsAndConditionsAll'].checked && keys.some(k => signatures[k].checked);
  }

  //#endregion
  //#region Change Detection
  // Deep config comparisons, change log generation, hasChangesFn, etc.

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['form']) {
      if (this.formChangesSub) {
        this.formChangesSub.unsubscribe();
      }

      this.formChangesSub = merge(this.form.controls['customerInformation'].valueChanges,
        this.form.controls['signatures'].valueChanges)
        .pipe(
          startWith(null),
          take(2))
        .subscribe(() => {

          // Update the signature states and images.
          const signatures = this.signatures();
          const termsKeys = Object.keys(signatures).filter(k => k.startsWith('termsAndConditions') && k !== 'termsAndConditionsAll') as SignatureMapKey[];

          // Update all the checkboxes based on what has been checked/set. 
          signatures['quotation'].checked = this.form.controls['signatures'].controls['signatureQuotation'].value !== null;
          signatures['termsAndConditions1'].checked = this.form.controls['signatures'].controls['agreeFirstInstalment'].value === true;
          signatures['termsAndConditions2'].checked = this.form.controls['signatures'].controls['agreeMarketing'].value === true;
          signatures['termsAndConditions3'].checked = this.form.controls['signatures'].controls['notUnderReview'].value === true;
          signatures['termsAndConditions4'].checked = this.form.controls['signatures'].controls['agreeTermsSpecsfin'].value === true;
          signatures['debitOrderMandate'].checked = this.form.controls['signatures'].controls['agreeMandate'].value === true;

          signatures['termsAndConditionsAll'].checked = termsKeys.every(k => signatures[k].checked);
          signatures['termsAndConditionsAll'].indeterminate = !signatures['termsAndConditionsAll'].checked && termsKeys.some(k => signatures[k].checked);

          // Update the images.
          signatures['termsAndConditionsAll'].serverUrl = this.form.controls['signatures'].controls['signatureTerms'].value?.contentUrl;
          signatures['quotation'].serverUrl = this.form.controls['signatures'].controls['signatureQuotation'].value?.contentUrl;
          signatures['debitOrderMandate'].serverUrl = this.form.controls['signatures'].controls['signatureMandate'].value?.contentUrl;

          // Update the required terms checkbox.
          const allRequiredTermsAreChecked = termsKeys.every(k => (signatures[k].required && signatures[k].checked) || !signatures[k].required);

          this.allRequiredTermsAreChecked.set(allRequiredTermsAreChecked);
          this.signatures.set(signatures);

        });
    }
  }

  //#endregion    
  //#region Parsing and Validation
  // Transformation/mapping methods
  // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

  public isTabTouched(key: PrimarySignatureKey) {
    const resourceField = this.toResourceField(key);
    const identityField = this.toIdField(key);

    return this.form.controls['signatures'].controls[resourceField].touched && this.form.controls['signatures'].controls[identityField].touched
  }

  public isTabValid(key: PrimarySignatureKey) {
    const resourceField = this.toResourceField(key);
    const identityField = this.toIdField(key);

    return this.form.controls['signatures'].controls[resourceField].value !== null && this.form.controls['signatures'].controls[identityField].value !== null;
  }


  private toFileName(key: PrimarySignatureKey): string {
    const customerId = this.form.controls['customerInformation'].controls['identityNumber'].value;
    const timestamp = new Date().getTime();

    switch (key) {
      case 'quotation':
        return `customer-quotation-${customerId}-${timestamp}.png`;
      case 'termsAndConditionsAll':
        return `customer-terms-and-conditions-${customerId}-${timestamp}.png`;
      case 'debitOrderMandate':
        return `customer-debit-order-mandate-${customerId}-${timestamp}.png`;
      default:
        return 'signature.png';
    }
  }

  private toResourceField(key: PrimarySignatureKey): keyof SignatureFormFields {
    switch (key) {
      case 'quotation':
        return 'signatureQuotation';
      case 'termsAndConditionsAll':
        return 'signatureTerms';
      case 'debitOrderMandate':
        return 'signatureMandate';
      default:
        throw new Error('Invalid signature key');
    }
  }

  private toIdField(key: PrimarySignatureKey): keyof SignatureFormFields {
    switch (key) {
      case 'quotation':
        return 'signatureQuotationId';
      case 'termsAndConditionsAll':
        return 'signatureTermsId';
      case 'debitOrderMandate':
        return 'signatureMandateId';
      default:
        throw new Error('Invalid signature key');
    }
  }

  //#endregion
  //#region Response Handlers
  // API/NGRX callback handlers.

  //#endregion
  //#region Navigation
  // Confirmation close dialogs, router guards, etc.

  //#endregion
  //#region Post Instantiation
  // Post-init fetch requests, used in such as landing page, after view init, etc.

  //#endregion

}

/**
 * The relevant fields for the signing process.
 * ALL fields on the form from the capture dialog are present as this is the master form.
 * The reduction of fields is to narrow interest and help prevent errors.
 */
interface RelevantFormGroup extends FormGroup<any> {
  controls: {
    /**
     * The customer's information.
     */
    customerInformation: FormGroup<PatientDetailsFormFields>

    /**
     * The signatures form.
     * Contains the customer's signature and the patient's signatures.
     */
    signatures: FormGroup<SignatureFormFields>

  }
}

/**
 * The signature map.
 * This is used to keep track of the signature checkboxes and their corresponding signatures.
 * Ignore the eslint rule as this isn't an error case.
*/
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SignatureMap extends Record<SignatureMapKey, SignatureMapEntry> { }

/**
 * The entry for the signature map.
 */
interface SignatureMapEntry {
  /**
   * Whether the checkbox is checked.
   */
  checked: boolean;

  /**
   * The base64 string of the signature.
   */
  base64?: string;

  /**
   * The server url of the signature.
   */
  serverUrl?: string;

  /**
   * Only required for the terms and conditions checkboxes. 
   */
  indeterminate?: boolean;

  /**
   * Whether the signature is required.
   */
  required: boolean;

  /**
   * The boolean form fields that should be updated when the checkbox is checked.
   */
  booleanFormFields?: Array<SignatureBooleanField>;
}

/**
 * The key for the signature map.
 */
type SignatureMapKey = 'quotation' | 'termsAndConditions1' | 'termsAndConditions2' | 'termsAndConditions3' | 'termsAndConditions4' | 'termsAndConditionsAll' | 'debitOrderMandate';

/**
 * Please refer to the SignatureFormFields for more information.
 * @see SignatureFormFields in libs/common/src/models/form.models.ts
 */
type SignatureBooleanField = 'agreeFirstInstalment' | 'agreeMandate' | 'agreeMarketing' | 'agreeTermsSpecsfin' | 'notUnderReview';

/**
 * The primary key for the signature object.
 */
type PrimarySignatureKey = 'quotation' | 'termsAndConditionsAll' | 'debitOrderMandate';